import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Collapse,
  IconButton,
  CircularProgress,
  Alert
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { MapContainer, TileLayer, Marker, Circle } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy, limit as firestoreLimit, startAfter, getCountFromServer } from 'firebase/firestore';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const mapContainerStyle = {
  width: '100%',
  height: '400px'
};

// Calculate distance between two coordinates in miles using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3958.8; // Earth's radius in miles
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) *
    Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in miles
};

const getLocationMatchStatus = (row) => {
  if (!row.currentLocation || !row.latitude || !row.longitude) {
    return { status: 'N/A', color: 'text.secondary' };
  }

  const distance = calculateDistance(
    row.latitude,
    row.longitude,
    row.currentLocation.latitude,
    row.currentLocation.longitude
  );

  if (distance < 0.01) { // Less than ~53 feet
    return { status: 'Identical', color: 'success.main', distance };
  } else if (distance < 0.1) { // Less than ~528 feet
    return { status: 'Very Close', color: 'info.main', distance };
  } else if (distance < 0.5) { // Less than ~2640 feet
    return { status: 'Close', color: 'warning.main', distance };
  } else {
    return { status: 'Different', color: 'error.main', distance };
  }
};

function WaterTempRow({ row }) {
  const [open, setOpen] = useState(false);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const center = {
    lat: row.latitude,
    lng: row.longitude
  };

  const locationMatch = getLocationMatchStatus(row);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{formatDate(row.dateTime)}</TableCell>
        <TableCell>{row.waterTemp}°F</TableCell>
        <TableCell>{row.createdByName || 'Unknown'}</TableCell>
        <TableCell>{row.stationId || 'N/A'}</TableCell>
        <TableCell>{row.lakeProfile || 'N/A'}</TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ color: locationMatch.color }}>
            {locationMatch.status}
            {locationMatch.distance !== undefined && (
              <Typography component="span" variant="caption" display="block" color="text.secondary">
                ({Math.round(locationMatch.distance)} mi)
              </Typography>
            )}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Document ID:</strong> {row.id}
                </Typography>
                {row.currentLocation && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Current Location:</strong> {row.currentLocation.latitude?.toFixed(6)}, {row.currentLocation.longitude?.toFixed(6)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Altitude:</strong> {row.currentLocation.altitude?.toFixed(2)}m
                      {' | '}
                      <strong>Accuracy:</strong> {row.currentLocation.accuracy?.toFixed(2)}m
                    </Typography>
                  </>
                )}
              </Box>

              <Box sx={{ height: 400, width: '100%', border: '1px solid #ddd' }}>
                <MapContainer
                  center={[center.lat, center.lng]}
                  zoom={14}
                  style={mapContainerStyle}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[center.lat, center.lng]} />
                  {row.currentLocation && (
                    <Circle
                      center={[row.currentLocation.latitude, row.currentLocation.longitude]}
                      radius={20}
                      pathOptions={{
                        fillColor: '#2196F3',
                        fillOpacity: 1,
                        color: '#ffffff',
                        weight: 2
                      }}
                    />
                  )}
                </MapContainer>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function WaterTemps() {
  const [waterTemps, setWaterTemps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const pageSnapshotsRef = useRef([]);

  const fetchWaterTemps = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Query Firestore directly
      const waterTempsRef = collection(db, 'water-temps');
      let q;
      
      if (page === 0) {
        // First page
        q = query(
          waterTempsRef,
          orderBy('dateTime', 'desc'),
          firestoreLimit(rowsPerPage)
        );
      } else if (pageSnapshotsRef.current[page - 1]) {
        // Use cached snapshot from previous page
        q = query(
          waterTempsRef,
          orderBy('dateTime', 'desc'),
          startAfter(pageSnapshotsRef.current[page - 1]),
          firestoreLimit(rowsPerPage)
        );
      } else {
        console.error('Page snapshot not found');
        return;
      }
      
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const lastDoc = snapshot.docs[snapshot.docs.length - 1];
        pageSnapshotsRef.current[page] = lastDoc;
      }
      
      // Get total count (for pagination) - only fetch once
      if (totalCount === 0) {
        const countSnapshot = await getCountFromServer(collection(db, 'water-temps'));
        const count = countSnapshot.data().count;
        setTotalCount(count);
      }
      
      const temps = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdByName: doc.data().createdBy || 'Unknown'
      }));
      
      setWaterTemps(temps);
    } catch (err) {
      console.error('Error fetching water temps:', err);
      setError(err.message || 'Failed to fetch water temperature data');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, totalCount]);

  React.useEffect(() => {
    fetchWaterTemps();
  }, [fetchWaterTemps]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    pageSnapshotsRef.current = []; // Reset page snapshots when changing rows per page
  };

  if (loading && waterTemps.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Water Temperatures
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell><strong>Date & Time</strong></TableCell>
                <TableCell><strong>Temperature</strong></TableCell>
                <TableCell><strong>Created By</strong></TableCell>
                <TableCell><strong>Station</strong></TableCell>
                <TableCell><strong>Lake Profile</strong></TableCell>
                <TableCell><strong>Location Match</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {waterTemps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No water temperature data found
                  </TableCell>
                </TableRow>
              ) : (
                waterTemps.map((row, index) => (
                  <WaterTempRow key={row.id || index} row={row} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
